import { DataResponse, InjectVariableOptions, SupportedDataType, UnknownSpec } from '@/core';
import { DataQuery } from '../runtime/DataQueryPlugins';
import { Plugin } from './plugin-base';
import { InjectGlobalFiltersResponse, QueryPluginContext } from './plugins';
import { VariableOpportunity, VariableOpportunityContext } from './variable-suggestions';

/**
 * An object containing all the dependencies of a data query.
 */
export type QueryPluginDependencies = {
  /**
   * Returns a list of variables name this query depends on.
   */
  variables?: string[];
};

/**
 * Response that is returned by basic data query plugins. The response will include data because
 * basic plugins fetch data once.
 *
 * See `QueryPluginGetProgressiveDataResponse` for progressive data query plugins.
 *
 * This type should be used by data query plugin implementations and framework processing.
 */
export type QueryPluginGetDataBasicResponse<T extends SupportedDataType = SupportedDataType> = DataResponse & {
  // We need the `& { type: T }` so we can pass in a generic to explicitly set the type of DataResponse we want.
  // Can be used in filtering like: GetDataResponse<'TimeSeries'> where only `TimeSeries` data responses are valid in the union.
  type: T;
};

type GetProgressiveDataOnUpdateOpts = {
  /**
   * Set to true to indicate data is done fetching. Use this when you know the data is done fetching when you perform
   * an update. Use `onComplete` if you determine the fetching is complete separate from the update.
   */
  isComplete?: boolean;
};

/**
 * Updater function that provides the previously cached data and returns updated results. This is
 * useful when a progressive query plugin needs to combine results because the backend only returns
 * pieces of data (instead of the latest full data).
 *
 * The previous data should be treated as immutable and MUST NOT be mutated. This is critical for
 * react-query to behave as expected. Consider using `immer`'s `produce` to safely work with the
 * data in an immutable way.
 * https://tanstack.com/query/latest/docs/reference/QueryClient/#queryclientsetquerydata
 */
type ProgressiveDataOnUpdateUpdaterFn<T extends SupportedDataType = SupportedDataType> = (
  // Intentionally not setting the generic datatype in here because (1) it was making the types
  // wonky in other spots that leverage this code (2) we cannot actually guarantee what data type is
  // saved in the react-query cache, so leaving it open-ended and requiring some type guarding is
  // actually more type-safe.
  prevData: Readonly<QueryPluginGetDataBasicResponse> | undefined
) => QueryPluginGetDataBasicResponse<T>;

/**
 * Callbacks used by progressive data query plugins to trigger updates to react-query progressively
 * via `queryFn` handling within the framework.
 *
 * These abstractions are needed because react-query does not support this functionality natively.
 */
export type GetProgressiveDataCallbacks<T extends SupportedDataType = SupportedDataType> = {
  /**
   * Use this callback to update the data. It currently supports two approaches:
   * - The updated data set. Useful when the full data is available on every update.
   * - Updater function. Useful when only the latest data is available on every update and it is
   *   necessary to stitch the results together on update.
   */
  onUpdate: (
    data: QueryPluginGetDataBasicResponse<T> | ProgressiveDataOnUpdateUpdaterFn<T>,
    opts?: GetProgressiveDataOnUpdateOpts
  ) => void;
  /**
   * Use this callback to indicate data is finished loading. Must be called when loading is completed
   * to ensure loading indicators in the UI are removed.
   */
  onComplete: () => void;
  /**
   * Use this callback to note that something went wrong. When this is called:
   * - The progressive query loading will be cancelled.
   * - The results will be left in their current state.
   * - The error message will be propagated to the results to be shown to the user.
   *
   * If you want to show error messages to consumers without stopping the progressive query, instead
   * add them to `metadata.notices` in the results when calling `onUpdate`.
   */
  onError: (error: unknown) => void;
};

/**
 * Response that is returned by progressive data query plugins. The response includes callbacks used
 * to progressively load data.
 *
 * See `QueryPluginGetBasicDataResponse` for basic data query plugins.
 */
export type QueryPluginGetDataProgressiveResponse<T extends SupportedDataType = SupportedDataType> = {
  /**
   * Function with callbacks that handle progressively loading the data. When this function is
   * called by the framework, the progressive query plugin should start its data fetching process
   * and then call the appropriate callbacks (e.g. `onUpdate` to trigger data updates) to communicate
   * changes to the framework, which will then make the appropriate updates to `react-query`.
   *
   * These abstractions are needed because react-query does not support this functionality natively.
   */
  getProgressiveData: (callbacks: GetProgressiveDataCallbacks<T>) => Promise<void> | void;
};

/**
 * Union of response types that can come back from query plugin `getData` functions.
 *
 * This type should be used by data query plugin implementations. For other use cases see:
 * - `InternalGetDataResponse`: for framework processing.
 * - `GetDataResponse`: for consumers of the framework using the data.
 */
export type QueryPluginGetDataResponse<T extends SupportedDataType = SupportedDataType> =
  | QueryPluginGetDataBasicResponse<T>
  | QueryPluginGetDataProgressiveResponse<T>;

/**
 * Additional metadata the framework uses on top of react-query in order to support the progressive
 * loading feature, which is not natively supported in react-query.
 */
export type InternalQueryMetadata = {
  /**
   * When true, additional data is being loaded via progressive loading. This prop is necessary
   * because react-query does not support this feature natively.
   */
  isFetchingMore?: boolean;

  /**
   * Error that was triggered during progressive loading. This prop is necessary because
   * react-query does not support this feature natively.
   */
  additionalError?: unknown;
};

/**
 * Response that is returned by the framework after processing data query plugins.
 *
 *  This type should be used for framework processing. For other use cases see:
 * - `QueryPluginGetDataResponse`: for data query plugin implementations.
 * - `GetDataResponse`: for consumers of the framework using the data via `DataQueriesProvider`.
 */
export type InternalGetDataResponse<T extends SupportedDataType = SupportedDataType> =
  QueryPluginGetDataBasicResponse<T> & {
    internalMetadata?: InternalQueryMetadata;
  };

/**
 * Response that is returned by the framework after processing data query plugin results in the
 * `DataQueriesProvider`.
 *
 * This type should be used by consumers of data from the framework. For other use cases see:
 * - `QueryPluginGetDataResponse`: for data query plugin implementations.
 * - `InternalGetDataResponse`: for framework processing.
 */
export type GetDataResponse<T extends SupportedDataType = SupportedDataType> = QueryPluginGetDataBasicResponse<T> & {
  isFetchingMore?: boolean;
};

export interface DataQueryInterface {
  new (): DataQuery;
}

/**
 * A plugin for running data queries.
 */
export interface DataQueryPlugin<Spec = UnknownSpec> extends Plugin<Spec> {
  getData: (spec: Spec, ctx: QueryPluginContext) => Promise<QueryPluginGetDataBasicResponse>;

  /*
    getComparableData gets the data the same as get data, but should also get data for comparison 
    to that based on the value specified in the QueryPluginContext compareDurationInMs 
  */
  getComparableData?: (spec: Spec, ctx: QueryPluginContext) => Promise<QueryPluginGetDataBasicResponse>;

  /**
   * A function that returns the dependencies of the query.
   * If the query depends on variables, it should return the list of variables it depends on.
   */
  dependsOn?: (spec: Spec, ctx: QueryPluginContext) => QueryPluginDependencies;

  /**
   * A function that returns the Spec with the variables interpolated.
   */
  interpolateVariables?: (spec: Spec, ctx: QueryPluginContext) => Spec;

  /**
   * A function that returns the Spec with the global filters injected.
   */
  injectGlobalFilters?: (spec: Spec, ctx: QueryPluginContext) => InjectGlobalFiltersResponse<Spec>;

  /**
   * Injects a variable reference into the query spec.
   * Used for bulk variable injection into panel queries.
   */
  injectVariable?: (spec: Spec, variable: InjectVariableOptions) => Promise<{ spec: Spec }>;

  /**
   * Extracts label names that a variable is used with in the query.
   * Used for showing context about how variables are used in panels.
   * @param spec The query spec to analyze
   * @param variableName The variable name to look for (without the $ prefix)
   * @returns Object containing the label names associated with this variable
   */
  extractVariableLabels?: (spec: Spec, variableName: string) => Promise<{ labelNames: string[] }>;

  /**
   * Analyzes a query spec against cross-dashboard context to find opportunities
   * where literal values could be replaced with dashboard variables.
   */
  getVariableOpportunities?: (spec: Spec, context: VariableOpportunityContext) => VariableOpportunity[];
}

/**
 * V2 of the data query plugin requires the plugin to be a class that extends an abstract class named DataQuery.
 */
export interface DataQueryPluginV2<Spec = UnknownSpec> extends Plugin<Spec> {
  plugin: DataQueryInterface;
}

/**
 * Creates a data query plugin based on the specified configuration.
 */
export function createDataQueryPlugin<DataQueryPluginSpec extends UnknownSpec>(
  config: DataQueryPlugin<DataQueryPluginSpec> | DataQueryPluginV2<DataQueryPluginSpec>
) {
  // Currently, this utility just passes back the config. Preemptively wrapping all data query
  // plugins will make it easier for us to give dev feedback and iterate on the interfaces in the
  // future.
  return config;
}
